import React, { useCallback, useState } from 'react';

import type { SkillFrontmatter, SkillFrontmatterError } from '@aline/common';
import { DISABLE_MODEL_INVOCATION_KEY, normalizeSkillName } from '@aline/common';
import { IconButton, InputBase, ListItemIcon, Menu, MenuItem, Typography } from '@mui/material';
import isEqual from 'lodash/isEqual';
import upperFirst from 'lodash/upperFirst';
import type { LucideIcon } from 'lucide-react';
import { BotOff, Plus, Puzzle, Scale, Tags, Wrench, X } from 'lucide-react';
import { AlineTooltip } from 'src/components';
import { ToggleSwitch } from 'src/components/generic/ToggleSwitch';
import { makeStyles } from 'tss-react/mui';

/**
 * Optional spec fields offered by "Add property". Anything else an imported skill carries
 * is still shown and kept; it just is not offered for adding here.
 */
const ADDABLE_PROPERTIES = {
  'allowed-tools': '',
  compatibility: '',
  // Added as `true`: the only reason to add the key is to keep Aline from picking the skill.
  [DISABLE_MODEL_INVOCATION_KEY]: true,
  license: '',
  metadata: {},
} satisfies Record<string, unknown>;

type AddableProperty = keyof typeof ADDABLE_PROPERTIES;

const ADDABLE_PROPERTY_ICONS: Record<AddableProperty, LucideIcon> = {
  'allowed-tools': Wrench,
  compatibility: Puzzle,
  [DISABLE_MODEL_INVOCATION_KEY]: BotOff,
  license: Scale,
  metadata: Tags,
};

type MetadataValue = string | number | boolean;

/**
 * One metadata entry as the author edits it. `value` keeps the type the file had (`1.0` is a
 * number) until the author types in it, so saving another entry leaves this one as written.
 */
interface MetadataRow {
  id: string;
  key: string;
  value: MetadataValue;
}

const PROPERTY_PLACEHOLDERS: Record<string, string> = {
  'allowed-tools': 'Space-separated tools, e.g. Read Bash(git:*)',
  compatibility: 'Environment requirements, e.g. Requires internet access',
  license: 'e.g. MIT, or the name of a bundled license file',
};

const useStyles = makeStyles()(theme => ({
  addButton: {
    '&:disabled': { cursor: 'default', opacity: 0.5 },
    '&:hover:not(:disabled)': { backgroundColor: theme.appColors.background },
    alignItems: 'center',
    alignSelf: 'flex-start',
    background: 'none',
    border: 0,
    borderRadius: 6,
    color: theme.palette.text.secondary,
    cursor: 'pointer',
    display: 'flex',
    font: 'inherit',
    fontSize: 12,
    gap: 4,
    padding: '4px 6px',
  },
  error: { color: theme.palette.error.main, fontSize: 12 },
  input: {
    '& textarea, & input': { padding: '4px 6px' },
    '&.Mui-error': { boxShadow: `inset 0 0 0 1px ${theme.palette.error.main}` },
    '&:hover:not(.Mui-disabled), &.Mui-focused': { backgroundColor: theme.appColors.background },
    borderRadius: 6,
    fontFamily: 'inherit',
    fontSize: 14,
    width: '100%',
  },
  key: {
    color: theme.palette.text.secondary,
    flexShrink: 0,
    fontFamily: 'inherit',
    fontSize: 14,
    fontWeight: 700,
    paddingTop: 6,
    width: 120,
  },
  metadataEntry: { alignItems: 'center', display: 'flex', gap: 4 },
  metadataKey: { flex: '0 0 35%' },
  metadataRow: { display: 'flex', flexDirection: 'column', gap: 2 },
  readOnlyValue: {
    color: theme.palette.text.secondary,
    fontFamily: 'inherit',
    fontSize: 12,
    paddingTop: 6,
  },
  root: {
    display: 'flex',
    flexDirection: 'column',
    fontFamily: 'inherit',
    gap: 2,
    padding: '24px 24px 0',
  },
  row: { alignItems: 'flex-start', display: 'flex', gap: 12 },
  rowRemove: { visibility: 'hidden' },
  rowWrap: {
    '&:hover .skill-property-remove, &:focus-within .skill-property-remove': {
      visibility: 'visible',
    },
  },
  // Lines the 20px toggle up with the 14px key text, which sits 6px down.
  switch: { marginTop: 6 },
  value: { display: 'flex', flex: 1, flexDirection: 'column', gap: 2, minWidth: 0 },
}));

const PropertyRow = React.memo(function PropertyRow({
  children,
  errors,
  label,
  onRemove,
}: {
  children: React.ReactNode;
  errors: SkillFrontmatterError[];
  label: string;
  onRemove?: (key: string) => void;
}) {
  const { classes, cx } = useStyles();
  const remove = useCallback(() => onRemove?.(label), [label, onRemove]);
  return (
    <div className={cx(classes.row, classes.rowWrap)}>
      <span className={classes.key} contentEditable={false}>
        {upperFirst(label)}:
      </span>
      <div className={classes.value}>
        {children}
        {errors
          .filter(error => error.field === label)
          .map(error => (
            <Typography key={error.message} className={classes.error} role="alert">
              {error.message}
            </Typography>
          ))}
      </div>
      {onRemove && (
        <AlineTooltip arrow placement="top" title={`Remove ${label}`}>
          <IconButton
            aria-label={`Remove ${label}`}
            className={cx(classes.rowRemove, 'skill-property-remove')}
            size="small"
            onClick={remove}
          >
            <X size={14} />
          </IconButton>
        </AlineTooltip>
      )}
    </div>
  );
});

const MetadataEntry = React.memo(function MetadataEntry({
  disabled,
  duplicate,
  index,
  onChange,
  onRemove,
  row,
}: {
  disabled: boolean;
  duplicate: boolean;
  index: number;
  onChange: (index: number, row: MetadataRow) => void;
  onRemove: (index: number) => void;
  row: MetadataRow;
}) {
  const { classes, cx } = useStyles();
  const changeKey = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      onChange(index, { ...row, key: event.target.value }),
    [index, onChange, row]
  );
  const changeValue = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      onChange(index, { ...row, value: event.target.value }),
    [index, onChange, row]
  );
  const remove = useCallback(() => onRemove(index), [index, onRemove]);
  return (
    <div className={classes.metadataRow}>
      <div className={classes.metadataEntry}>
        <InputBase
          className={cx(classes.input, classes.metadataKey)}
          disabled={disabled}
          error={duplicate}
          inputProps={{ 'aria-invalid': duplicate, 'aria-label': 'Metadata key' }}
          value={row.key}
          onChange={changeKey}
        />
        <InputBase
          className={classes.input}
          disabled={disabled}
          inputProps={{ 'aria-label': `Metadata ${row.key}` }}
          value={String(row.value)}
          onChange={changeValue}
        />
        {!disabled && (
          <IconButton aria-label={`Remove metadata ${row.key}`} size="small" onClick={remove}>
            <X size={14} />
          </IconButton>
        )}
      </div>
      {duplicate && (
        <Typography className={classes.error} role="alert">
          {row.key ? 'Duplicate key' : 'Enter a key'}
        </Typography>
      )}
    </div>
  );
});

/**
 * `metadata` as editable key/value rows.
 *
 * The rows live here rather than in the file, because two rows can briefly share a key
 * while the author types one ('key-2' retyped as 'author2' passes through 'author'). A map
 * cannot hold both, so writing that out would drop a value. Rows are written back only
 * while every key is unique and non-empty; until then the clashing rows show an error.
 */
const MetadataEditor = React.memo(function MetadataEditor({
  disabled,
  onChange,
  value,
}: {
  disabled: boolean;
  onChange: (value: Record<string, MetadataValue>) => void;
  value: Record<string, MetadataValue>;
}) {
  const { classes } = useStyles();
  const [rows, setRows] = useState(() => getMetadataRows(value));
  const [syncedValue, setSyncedValue] = useState(value);
  // A reload or an external save replaces the values shown in these rows.
  if (!isEqual(value, syncedValue)) {
    setSyncedValue(value);
    if (!isEqual(getMetadataRecord(rows), value)) setRows(getMetadataRows(value));
  }
  const invalidKeys = getInvalidMetadataKeys(rows);
  const commit = useCallback(
    (next: MetadataRow[]) => {
      setRows(next);
      const record = getMetadataRecord(next);
      if (record) onChange(record);
    },
    [onChange]
  );
  const change = useCallback(
    (index: number, row: MetadataRow) =>
      commit(rows.map((current, position) => (position === index ? row : current))),
    [commit, rows]
  );
  const remove = useCallback(
    (index: number) => commit(rows.filter((_row, position) => position !== index)),
    [commit, rows]
  );
  // A fresh key rather than an empty one, so the new row is valid and saved right away.
  const add = useCallback(
    () => commit([...rows, { id: crypto.randomUUID(), key: getNewMetadataKey(rows), value: '' }]),
    [commit, rows]
  );
  return (
    <>
      {rows.map((row, index) => (
        <MetadataEntry
          key={row.id}
          disabled={disabled}
          duplicate={invalidKeys.has(row.key)}
          index={index}
          row={row}
          onChange={change}
          onRemove={remove}
        />
      ))}
      {!disabled && (
        <button className={classes.addButton} type="button" onClick={add}>
          <Plus size={14} />
          Add entry
        </button>
      )}
    </>
  );
});

const BooleanPropertyValue = React.memo(function BooleanPropertyValue({
  disabled,
  onChange,
  propertyKey,
  value,
}: {
  disabled: boolean;
  onChange: (key: string, value: unknown) => void;
  propertyKey: string;
  value: boolean;
}) {
  const { classes } = useStyles();
  const toggle = useCallback(() => onChange(propertyKey, !value), [onChange, propertyKey, value]);
  const toggleWithKey = useCallback(
    (event: React.KeyboardEvent) => {
      if (disabled || (event.key !== ' ' && event.key !== 'Enter')) return;
      event.preventDefault();
      toggle();
    },
    [disabled, toggle]
  );
  return (
    <ToggleSwitch
      aria-checked={value}
      aria-disabled={disabled}
      aria-label={propertyKey}
      className={classes.switch}
      disabled={disabled}
      role="switch"
      tabIndex={disabled ? -1 : 0}
      toggle={toggle}
      toggled={value}
      onKeyDown={toggleWithKey}
    />
  );
});

/**
 * One optional property's value. Text is edited in place and `metadata` as key/value pairs,
 * the two shapes the spec defines. Anything else an imported skill carries is shown read-only
 * and preserved unchanged when the skill is saved.
 */
const PropertyValue = React.memo(function PropertyValue({
  disabled,
  onChange,
  propertyKey,
  value,
}: {
  disabled: boolean;
  onChange: (key: string, value: unknown) => void;
  propertyKey: string;
  value: unknown;
}) {
  const { classes } = useStyles();
  const changeText = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      onChange(propertyKey, event.target.value),
    [onChange, propertyKey]
  );
  const changeMetadata = useCallback(
    (metadata: Record<string, MetadataValue>) => onChange(propertyKey, metadata),
    [onChange, propertyKey]
  );

  if (typeof value === 'string')
    return (
      <InputBase
        multiline
        className={classes.input}
        disabled={disabled}
        inputProps={{ 'aria-label': propertyKey }}
        placeholder={PROPERTY_PLACEHOLDERS[propertyKey]}
        value={value}
        onChange={changeText}
      />
    );
  if (typeof value === 'boolean')
    return (
      <BooleanPropertyValue
        disabled={disabled}
        propertyKey={propertyKey}
        value={value}
        onChange={onChange}
      />
    );
  if (propertyKey === 'metadata' && getIsScalarRecord(value))
    return <MetadataEditor disabled={disabled} value={value} onChange={changeMetadata} />;
  return <Typography className={classes.readOnlyValue}>{JSON.stringify(value)}</Typography>;
});

const AddPropertyItem = React.memo(function AddPropertyItem({
  onAdd,
  property,
}: {
  onAdd: (property: AddableProperty) => void;
  property: AddableProperty;
}) {
  const add = useCallback(() => onAdd(property), [onAdd, property]);
  const Icon = ADDABLE_PROPERTY_ICONS[property];
  return (
    <MenuItem onClick={add}>
      <ListItemIcon>
        <Icon size={18} />
      </ListItemIcon>
      {upperFirst(property)}
    </MenuItem>
  );
});

/**
 * The SKILL.md frontmatter as editable properties at the top of the file, the way a notes
 * app shows a page's properties. `name` and `description` are always there; every other key
 * the file carries gets a row of its own, so nothing an imported skill brought is hidden.
 */
export const SkillProperties = React.memo(function SkillProperties({
  disabled,
  errors,
  frontmatter,
  onChange,
}: {
  disabled: boolean;
  errors: SkillFrontmatterError[];
  frontmatter: SkillFrontmatter;
  onChange: (frontmatter: SkillFrontmatter) => void;
}) {
  const { classes } = useStyles();
  const [addAnchor, setAddAnchor] = useState<HTMLElement | null>(null);
  const openAdd = useCallback(
    (event: React.MouseEvent<HTMLElement>) => setAddAnchor(event.currentTarget),
    []
  );
  const closeAdd = useCallback(() => setAddAnchor(null), []);
  const changeName = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) =>
      onChange({ ...frontmatter, name: normalizeSkillName(event.target.value, { partial: true }) }),
    [frontmatter, onChange]
  );
  const finishName = useCallback(() => {
    const name = normalizeSkillName(frontmatter.name);
    if (name !== frontmatter.name) onChange({ ...frontmatter, name });
  }, [frontmatter, onChange]);
  const changeDescription = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) =>
      onChange({ ...frontmatter, description: event.target.value }),
    [frontmatter, onChange]
  );
  const changeExtra = useCallback(
    (key: string, value: unknown) =>
      onChange({ ...frontmatter, extra: { ...frontmatter.extra, [key]: value } }),
    [frontmatter, onChange]
  );
  const removeExtra = useCallback(
    (key: string) =>
      onChange({
        ...frontmatter,
        extra: Object.fromEntries(Object.entries(frontmatter.extra).filter(([k]) => k !== key)),
      }),
    [frontmatter, onChange]
  );
  const nameInvalid = errors.some(error => error.field === 'name');
  const descriptionInvalid = errors.some(error => error.field === 'description');
  const addableKeys = Object.keys(ADDABLE_PROPERTIES)
    .filter(getIsAddableProperty)
    .filter(key => !Object.hasOwn(frontmatter.extra, key));
  const addProperty = useCallback(
    (key: AddableProperty) => {
      setAddAnchor(null);
      changeExtra(key, ADDABLE_PROPERTIES[key]);
    },
    [changeExtra]
  );

  return (
    <section aria-label="Skill frontmatter" className={classes.root} contentEditable={false}>
      <span aria-hidden="true">---</span>
      <PropertyRow errors={errors} label="name">
        <InputBase
          className={classes.input}
          disabled={disabled}
          error={nameInvalid}
          inputProps={{ 'aria-invalid': nameInvalid, 'aria-label': 'Skill name' }}
          placeholder="lowercase-with-hyphens, e.g. nda-review"
          value={frontmatter.name}
          onBlur={finishName}
          onChange={changeName}
        />
      </PropertyRow>
      <PropertyRow errors={errors} label="description">
        <InputBase
          multiline
          className={classes.input}
          disabled={disabled}
          error={descriptionInvalid}
          inputProps={{ 'aria-invalid': descriptionInvalid, 'aria-label': 'Skill description' }}
          placeholder="What the skill does and when Aline should use it"
          value={frontmatter.description}
          onChange={changeDescription}
        />
      </PropertyRow>
      {Object.entries(frontmatter.extra).map(([key, value]) => (
        <PropertyRow
          key={key}
          errors={errors}
          label={key}
          onRemove={disabled ? undefined : removeExtra}
        >
          <PropertyValue
            disabled={disabled}
            propertyKey={key}
            value={value}
            onChange={changeExtra}
          />
        </PropertyRow>
      ))}
      <span aria-hidden="true">---</span>
      {!disabled && Boolean(addableKeys.length) && (
        <>
          <button className={classes.addButton} type="button" onClick={openAdd}>
            <Plus size={14} />
            Add property
          </button>
          <Menu anchorEl={addAnchor} open={Boolean(addAnchor)} onClose={closeAdd}>
            {addableKeys.map(key => (
              <AddPropertyItem key={key} property={key} onAdd={addProperty} />
            ))}
          </Menu>
        </>
      )}
    </section>
  );
});

function getIsAddableProperty(key: string): key is AddableProperty {
  return Object.hasOwn(ADDABLE_PROPERTIES, key);
}

function getMetadataRows(value: Record<string, MetadataValue>): MetadataRow[] {
  return Object.entries(value).map(([key, entry]) => ({
    id: crypto.randomUUID(),
    key,
    value: entry,
  }));
}

/** The rows as the map the file holds, or `null` while a key is empty or repeated. */
function getMetadataRecord(rows: MetadataRow[]): Record<string, MetadataValue> | null {
  if (getInvalidMetadataKeys(rows).size) return null;
  return Object.fromEntries(rows.map(row => [row.key, row.value]));
}

/** Keys that cannot be written: an empty one, or one more than one row uses. */
function getInvalidMetadataKeys(rows: MetadataRow[]): Set<string> {
  const seen = new Set<string>();
  const invalid = new Set<string>();
  for (const { key } of rows) {
    if (!key || seen.has(key)) invalid.add(key);
    seen.add(key);
  }
  return invalid;
}

function getNewMetadataKey(rows: MetadataRow[]): string {
  const keys = new Set(rows.map(row => row.key));
  let index = keys.size + 1;
  while (keys.has(`key-${index}`)) index++;
  return `key-${index}`;
}

function getIsScalarRecord(value: unknown): value is Record<string, MetadataValue> {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    Object.values(value).every(
      entry => typeof entry === 'string' || typeof entry === 'number' || typeof entry === 'boolean'
    )
  );
}
